import {
  reasonerDefaultModel,
  unstructuredDataLoaderDefaultModel,
  enricherDefaultModel,
  linkingDefaultModel,
  embeddingDefaultModel,
  showModelSelector,
} from '@/config/env';
import type {
  SubagentConfig,
  CompactionConfig,
} from '@/interfaces/reasoner.interface';

export interface ReasonerModelOption {
  label: string;
  value: string;

  // Agent concerns -- same nested structure as backend BaseAgentConfig
  /**
   * Token budget settings for subagent execution.
   * max_tokens: conversation token ceiling.
   * hard_limit_tokens: count at which forced final response triggers.
   */
  subagent: SubagentConfig;
  /**
   * Settings controlling conversation compaction (summarization).
   * threshold_tokens: input token count at which compaction triggers.
   *   0 = compaction disabled.
   *   Shared-pool models: context_window - max_output_tokens.
   *   Independent-limit models (OSS, Gemma): context_window.
   * messages_to_keep: recent messages preserved after compaction.
   * min_buffer_tokens: minimum buffer after summarization to avoid
   *   immediate re-summarization.
   * input_limit_tokens: max tokens when trimming for summary generation.
   */
  compaction: CompactionConfig;
  /**
   * Maximum tokens allowed for a single tool result payload.
   * Trimmed to this limit before being passed back to the model.
   * Use smaller values (e.g. 5000) for models with tight context windows.
   */
  tool_max_tokens: number;

  // Model concerns -- raw LLM constructor params forwarded to get_llm.
  // OpenRouter: pass routing as openrouter_provider or extra_body.provider
  // (ChatOpenRouter). Do not use a top-level provider key.
  metadata: Record<string, unknown>;
}

export interface ModelOption {
  label: string;
  value: string;
  /** Optional metadata; OpenRouter routing belongs under ``openrouter_provider``
   *  or ``extra_body.provider`` (LangChain ``ChatOpenRouter``), not a
   *  top-level ``provider`` key.
   */
  metadata?: {
    maxTokens?: number; // Max output tokens for this model
    contextWindow?: number; // Total context window size (input + output)
  } & Record<string, unknown>;
}

export interface AgentTypeDefaults {
  modelVersion?: string;
}

export interface AgentTypeConfig {
  models: ModelOption[];
  defaults?: AgentTypeDefaults;
}

export interface ModelsConfig {
  services: {
    reasoner: {
      models: ReasonerModelOption[];
    };
    unstructuredDataLoader: {
      agentTypes: {
        LangChain: AgentTypeConfig;
        LangChainTxt: AgentTypeConfig;
        LangChainJson: AgentTypeConfig;
      };
    };
    enricher: {
      models: ModelOption[];
      default: string;
      langchainModels: ModelOption[];
    };
    linking: {
      models: ModelOption[];
      default: string;
      langchainModels: ModelOption[];
    };
    embeddings: {
      models: ModelOption[];
    };
  };
}

export interface UnstructuredDataLoaderDefaults {
  modelName: string;
  modelVersion?: string;
}

export const AGENT_TYPES = {
  LANGCHAIN: 'LangChain' as const,
  LANGCHAIN_TXT: 'LangChainTxt' as const,
  LANGCHAIN_JSON: 'LangChainJson' as const,
} as const;

export const EMBEDDINGS_API_TYPES = {
  EMBEDDINGS_API: 'embeddings_api' as const,
} as const;

export const LINKING_AGENT_TYPES = {
  LANGCHAIN: 'langchain' as const,
} as const;

export const ENRICHER_PROVIDER_TYPES = {
  LANGCHAIN: 'langchain' as const,
} as const;

export const LINKING_STRATEGY_TYPES = {
  EXACT_MATCH: 'exact_match' as const,
  EMBEDDING_SIMILARITY: 'embedding_similarity' as const,
  JACCARD_SIMILARITY: 'jaccard_similarity' as const,
  LLM: 'llm' as const,
  NONE: 'none' as const,
} as const;

export type AgentType = (typeof AGENT_TYPES)[keyof typeof AGENT_TYPES];
export type LinkingAgentType =
  (typeof LINKING_AGENT_TYPES)[keyof typeof LINKING_AGENT_TYPES];
export type EnricherProviderType =
  (typeof ENRICHER_PROVIDER_TYPES)[keyof typeof ENRICHER_PROVIDER_TYPES];

const VALID_AGENT_TYPES: readonly AgentType[] = Object.values(AGENT_TYPES);

/**
 * Maps extraction agent type when reading from API payloads.
 * TODO(review_api): Remove when review_api DTO no longer returns Azure types.
 * Until then, legacy AzureOpenAI/AzureTxt/AzureJson are mapped to LangChain equivalents.
 * Unknown types are rejected and defaultType is returned (defense in depth).
 */
export const extractionAgentTypeFromApi = (
  type: string | undefined,
  defaultType: AgentType
): AgentType => {
  if (type === 'AzureOpenAI') return AGENT_TYPES.LANGCHAIN;
  if (type === 'AzureTxt') return AGENT_TYPES.LANGCHAIN_TXT;
  if (type === 'AzureJson') return AGENT_TYPES.LANGCHAIN_JSON;
  if (type && VALID_AGENT_TYPES.includes(type as AgentType)) {
    return type as AgentType;
  }
  return defaultType;
};

export type ServiceType = keyof ModelsConfig['services'];
export const SERVICES = {
  REASONER: 'reasoner' as const,
  UNSTRUCTURED_DATA_LOADER: 'unstructuredDataLoader' as const,
  ENRICHER: 'enricher' as const,
  LINKING: 'linking' as const,
  EMBEDDINGS: 'embeddings' as const,
};

const createLangChainConfig = (): AgentTypeConfig => ({
  models: [
    {
      label: 'Claude Opus 5',
      value: 'anthropic:claude-opus-5',
    },
    {
      label: 'Claude Opus 5.5',
      value: 'anthropic:claude-opus-5-5',
    },
    {
      label: 'Claude Opus 4.6',
      value: 'anthropic:claude-opus-4-6',
    },
    {
      label: 'Claude Sonnet 5',
      value: 'anthropic:claude-sonnet-5',
    },
    {
      label: 'Claude Sonnet 5.5',
      value: 'anthropic:claude-sonnet-5-5',
    },
    {
      label: 'Claude Haiku 4.5',
      value: 'anthropic:claude-haiku-4-5-20251001',
    },
    {
      label: 'Anthropic Opus 4.1',
      value: 'anthropic:claude-opus-4-1-20250805',
    },
    { label: 'Azure ChatGPT GPT4o', value: 'azure:GPT4o:2024-08-01-preview' },
    {
      label: 'Azure Foundry ChatGPT 4-1',
      value: 'azure_foundry:gpt-4.1:2024-12-01-preview',
    },
    { label: 'Ollama GPT-OSS 20B', value: 'ollama:gpt-oss:20b' },
    { label: 'OpenRouter GPT-OSS:120B', value: 'openrouter:openai/gpt-oss-120b' },
    {
      label: 'OpenRouter gpt-oss-20b (DeepInfra BF16)',
      value: 'openrouter:openai/gpt-oss-20b',
      metadata: {
        openrouter_provider: {
          only: ['deepinfra'],
          quantizations: ['bf16'],
          allow_fallbacks: false,
        },
      },
    },
    {
      label: 'OpenRouter gpt-oss-safeguard-20b (Nitro)',
      value: 'openrouter:openai/gpt-oss-safeguard-20b:nitro',
    },
    {
      label: 'OpenRouter gpt-oss:120b (DeepInfra BF16)',
      value: 'openrouter:openai/gpt-oss-120b',
      metadata: {
        openrouter_provider: {
          only: ['deepinfra'],
          quantizations: ['bf16'],
          allow_fallbacks: false,
        },
      },
    },
    { label: 'OpenRouter GPT-5.4', value: 'openrouter:openai/gpt-5.4' },
    {
      label: 'OpenRouter Gemma 4 31B (Novita BF16)',
      value: 'openrouter:google/gemma-4-31b-it',
      metadata: {
        openrouter_provider: {
          only: ['novita'],
          quantizations: ['bf16'],
          allow_fallbacks: false,
        },
      },
    },
    {
      label: 'OpenRouter Gemma 4 26B A4B (Parasail BF16)',
      value: 'openrouter:google/gemma-4-26b-a4b-it',
      metadata: {
        openrouter_provider: {
          only: ['parasail'],
          quantizations: ['bf16'],
          allow_fallbacks: false,
        },
      },
    },
    {
      label: 'OpenRouter Llama 4 Maverick',
      value: 'openrouter:meta-llama/llama-4-maverick',
    },
    {
      label: 'OpenRouter Llama 4 Scout',
      value: 'openrouter:meta-llama/llama-4-scout',
    },
    {
      label: 'OpenRouter Devstral 2512',
      value: 'openrouter:mistralai/devstral-2512',
    },
    {
      label: 'OpenRouter Ministral 3 14B 2512',
      value: 'openrouter:mistralai/ministral-14b-2512',
    },
    {
      label: 'OpenRouter Muse Glimmer 30B (Together)',
      value: 'openrouter:meta/muse-glimmer-30b',
      metadata: {
        // Three endpoints serve this model and the other two are rejected,
        // so the label's "(Together)" has to be enforced, not just claimed.
        // No quantizations filter: Together reports precision "--"
        // (unknown), which any filter would exclude.
        openrouter_provider: {
          only: ['together'],
          allow_fallbacks: false,
        },
      },
    },
    {
      label: 'OpenRouter Nemotron 3.5 Lightning (Free)',
      value: 'openrouter:nvidia/nemotron-3.5-lightning:free',
    },
  ],
});

const modelsConfig: ModelsConfig = {
  services: {
    reasoner: {
      models: [
        // --- Anthropic (ChatAnthropic) ---
        // Raw params: max_tokens, thinking, output_config
        {
          label: 'Claude Opus 5',
          value: 'anthropic:claude-opus-5',
          subagent: { max_tokens: 800000, hard_limit_tokens: 700000 },
          compaction: {
            threshold_tokens: 800000,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 500000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 128000,
            thinking: { type: 'adaptive' },
            output_config: { effort: 'max' },
          },
        },
        {
          label: 'Claude Opus 5.5',
          value: 'anthropic:claude-opus-5-5',
          subagent: { max_tokens: 800000, hard_limit_tokens: 700000 },
          compaction: {
            threshold_tokens: 800000,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 500000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 128000,
            thinking: { type: 'adaptive' },
            output_config: { effort: 'max' },
          },
        },
        {
          label: 'Claude Opus 4.8 1M',
          value: 'anthropic:claude-opus-4-8',
          subagent: { max_tokens: 800000, hard_limit_tokens: 700000 },
          compaction: {
            threshold_tokens: 800000,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 500000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 128000,
            thinking: { type: 'adaptive' },
            output_config: { effort: 'max' },
          },
        },
        {
          label: 'Claude Opus 4.7 1M',
          value: 'anthropic:claude-opus-4-7',
          subagent: { max_tokens: 800000, hard_limit_tokens: 700000 },
          compaction: {
            threshold_tokens: 800000,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 500000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 128000,
            thinking: { type: 'adaptive' },
            output_config: { effort: 'max' },
          },
        },
        {
          label: 'Claude Opus 4.6 1M',
          value: 'anthropic:claude-opus-4-6',
          subagent: { max_tokens: 800000, hard_limit_tokens: 700000 },
          compaction: {
            threshold_tokens: 800000,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 500000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 128000,
            thinking: { type: 'adaptive' },
            output_config: { effort: 'max' },
          },
        },
        {
          label: 'Claude Sonnet 5',
          value: 'anthropic:claude-sonnet-5',
          subagent: { max_tokens: 800000, hard_limit_tokens: 700000 },
          compaction: {
            threshold_tokens: 800000,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 500000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 64000,
            thinking: { type: 'adaptive' },
            output_config: { effort: 'max' },
          },
        },
        {
          label: 'Claude Sonnet 5.5',
          value: 'anthropic:claude-sonnet-5-5',
          subagent: { max_tokens: 800000, hard_limit_tokens: 700000 },
          compaction: {
            threshold_tokens: 800000,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 500000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 64000,
            thinking: { type: 'adaptive' },
            output_config: { effort: 'max' },
          },
        },
        {
          label: 'Claude Sonnet 4.6',
          value: 'anthropic:claude-sonnet-4-6',
          subagent: { max_tokens: 800000, hard_limit_tokens: 700000 },
          compaction: {
            threshold_tokens: 800000,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 500000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 64000,
            thinking: { type: 'adaptive' },
            output_config: { effort: 'max' },
          },
        },
        {
          label: 'Claude Haiku 4.5',
          value: 'anthropic:claude-haiku-4-5-20251001',
          subagent: { max_tokens: 200000, hard_limit_tokens: 160000 },
          compaction: {
            threshold_tokens: 108800,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 54400,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 64000,
          },
        },
        // --- Bedrock (ChatBedrockConverse) ---
        // Raw params: max_tokens, additional_model_request_fields
        {
          label: 'AWS Bedrock Sonnet 4',
          value:
            'bedrock:arn:aws:bedrock:us-east-2:885066071120:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
          subagent: { max_tokens: 200000, hard_limit_tokens: 160000 },
          compaction: {
            threshold_tokens: 108800,
            messages_to_keep: 5,
            min_buffer_tokens: 21760,
            input_limit_tokens: 54400,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 64000,
            additional_model_request_fields: {
              thinking: { type: 'enabled', budget_tokens: 8000 },
            },
          },
        },
        // --- On-prem vLLM (ChatOpenAI via the `openai` vendor) ---
        // Served by the customer L40S box. The base_url comes from
        // MODEL_CONFIG's `openai.endpoint`, so this entry only works while
        // that points at the box (locally: scripts/vllm-proxy.py on
        // http://localhost:34900/v1, which adds the Host header the ingress
        // routes on and absorbs its self-signed cert).
        //
        // Deployed limits, read live from /v1/models and /metrics:
        //   max_model_len  131072 -- a SHARED prompt+output pool
        //   max_new_tokens  16384 -- server-wide output cap; clamps every
        //     request regardless of what the client asks for
        //   max_num_seqs        1 -- one request at a time, rest queue FIFO
        // Shared pool, so compaction triggers at 131072 - 16384 = 114688.
        // The model spends output tokens on channel-scoped reasoning before
        // emitting content, so 16384 covers reasoning + answer.
        {
          label: 'On-prem vLLM Muse Glimmer 30B (L40S)',
          value: 'openai:muse-glimmer',
          // Conversation ceiling and forced-final-response point, NOT the
          // output cap -- that is `metadata.max_tokens` below. Sized off the
          // 131072 shared pool (hard limit at 80%, as on the OpenRouter entry
          // for this same model). The earlier 16384/14000 were the output
          // numbers in a conversation-budget slot: they forced a final
          // response at 14k of context and put the 114688 compaction
          // threshold permanently out of reach.
          subagent: { max_tokens: 131072, hard_limit_tokens: 104858 },
          compaction: {
            threshold_tokens: 114688,
            messages_to_keep: 5,
            min_buffer_tokens: 20000,
            input_limit_tokens: 50000,
          },
          tool_max_tokens: 5000,
          // Reasoner metadata is forwarded verbatim as ChatOpenAI
          // constructor kwargs, so only real constructor params belong
          // here -- a UI-level key like contextWindow would be passed
          // straight through to the client. The 131072 window is
          // expressed by compaction.threshold_tokens above.
          metadata: {
            max_tokens: 16384,
          },
        },
        {
          label: 'OpenRouter gpt-oss-20b (DeepInfra BF16)',
          value: 'openrouter:openai/gpt-oss-20b',
          subagent: { max_tokens: 40000, hard_limit_tokens: 32000 },
          compaction: {
            threshold_tokens: 100000,
            messages_to_keep: 5,
            min_buffer_tokens: 20000,
            input_limit_tokens: 50000,
          },
          tool_max_tokens: 5000,
          metadata: {
            max_tokens: 40000,
            reasoning: {
              // effort: 'low'
              effort: 'medium'
            },
            openrouter_provider: {
              only: ['deepinfra'],
              quantizations: ['bf16'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter gpt-oss-safeguard-20b (Nitro)',
          value: 'openrouter:openai/gpt-oss-safeguard-20b:nitro',
          subagent: { max_tokens: 40000, hard_limit_tokens: 32000 },
          compaction: {
            threshold_tokens: 100000,
            messages_to_keep: 5,
            min_buffer_tokens: 20000,
            input_limit_tokens: 50000,
          },
          tool_max_tokens: 5000,
          metadata: {
            max_tokens: 40000,
            reasoning: { effort: 'medium' },
          },
        },
        {
          label: 'OpenRouter gpt-oss:120b (DeepInfra BF16)',
          value: 'openrouter:openai/gpt-oss-120b',
          subagent: { max_tokens: 40000, hard_limit_tokens: 32000 },
          compaction: {
            threshold_tokens: 100000,
            messages_to_keep: 5,
            min_buffer_tokens: 20000,
            input_limit_tokens: 50000,
          },
          tool_max_tokens: 10000,
          metadata: {
            max_tokens: 40000,
            reasoning: { effort: 'low' },
            openrouter_provider: {
              only: ['deepinfra'],
              quantizations: ['bf16'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter GPT-5.4',
          value: 'openrouter:openai/gpt-5.4',
          subagent: { max_tokens: 900000, hard_limit_tokens: 800000 },
          compaction: {
            threshold_tokens: 922000,
            messages_to_keep: 16,
            min_buffer_tokens: 64000,
            input_limit_tokens: 500000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 128000,
            reasoning: { effort: 'xhigh' },
          },
        },
        {
          label: 'OpenRouter Gemma 4 31B (Crusoe)',
          value: 'openrouter:google/gemma-4-31b-it',
          // NOT VIABLE WITH THE MCP TOOLSET AS CONFIGURED (2026-08-19).
          // review_mcp's schema set is now ~20.8k prompt tokens (9 tools;
          // ~22.5k with search), which is 3.5x this entry's
          // input_limit_tokens of 6000 -- the tool schemas alone overflow
          // the whole input budget before a single message is counted, and
          // the compaction tracker does not see them. The budgets below
          // date from the 2026-08-11 bench against a 76.5 KB / ~19.0k set
          // and were already tight. Either repoint this label at a
          // larger-context endpoint and re-derive the four numbers from a
          // bench, or drop it from the MCP-capable list. Left as-is rather
          // than guessed at: changing them without a bench run just moves
          // the failure somewhere less obvious.
          subagent: { max_tokens: 250000, hard_limit_tokens: 200000 },
          compaction: {
            threshold_tokens: 12000,
            messages_to_keep: 5,
            min_buffer_tokens: 2400,
            input_limit_tokens: 6000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 80000,
            reasoning: { effort: 'high' },
            openrouter_provider: {
              // Crusoe: 262144 ctx / 262141 max output, uptime ~99.8%/30m.
              // Verified live against the real MCP schema set as of the
              // 2026-08-11 bench (6 tools then; 9 now -- see the size note
              // on muse-glimmer-30b below).
              //
              // NO quantizations filter: Crusoe reports quantization
              // "unknown", so any filter (including ['bf16']) excludes it --
              // and with allow_fallbacks false that fails every request
              // outright rather than degrading. Re-add the filter only
              // alongside a pin that actually reports its quantization.
              //
              // Provider must accept `oneOf` in a tool schema (describe's
              // targets[] union; review_mcp builds it in
              // backend/tools/tool_definitions.py -- the run_pipelines
              // description itself now lives in
              // backend/tools/run_pipelines_docs.py).
              // Providers whose tool-call path compiles a grammar with the
              // xgrammar backend reject it outright:
              //   modelrun  400 "unsupported schema keyword: oneOf"
              // Also verified OK on the real payload: coreweave (bf16,
              // 262144 ctx / 262144 out, $0.10/$0.34 per M), novita
              // (131072 max output only), siliconflow (uptime ~14%/30m).
              // sambanova: 131072 ctx, under the subagent ceiling above.
              // only: ['modelrun'],     // 400s on oneOf tool schema
              // only: ['coreweave'],    // needs quantizations: ['bf16']
              // only: ['novita'],
              // only: ['sambanova'],
              only: ['crusoe'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter Gemma 4 31B (Free)',
          value: 'openrouter:google/gemma-4-31b-it:free',
          subagent: { max_tokens: 250000, hard_limit_tokens: 200000 },
          compaction: {
            threshold_tokens: 12000,
            messages_to_keep: 5,
            min_buffer_tokens: 2400,
            input_limit_tokens: 6000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 80000,
            reasoning: { effort: 'high' },
          },
        },
        {
          label: 'OpenRouter Gemma 4 26B A4B (Parasail BF16)',
          value: 'openrouter:google/gemma-4-26b-a4b-it',
          subagent: { max_tokens: 250000, hard_limit_tokens: 200000 },
          compaction: {
            threshold_tokens: 12000,
            messages_to_keep: 5,
            min_buffer_tokens: 2400,
            input_limit_tokens: 6000,
          },
          tool_max_tokens: 20000,
          metadata: {
            max_tokens: 80000,
            reasoning: { effort: 'high' },
            openrouter_provider: {
              only: ['parasail'],
              quantizations: ['bf16'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter Llama 4 Maverick',
          value: 'openrouter:meta-llama/llama-4-maverick',
          subagent: { max_tokens: 1048576, hard_limit_tokens: 838861 },
          compaction: {
            threshold_tokens: 787660,
            messages_to_keep: 20,
            min_buffer_tokens: 157532,
            input_limit_tokens: 393830,
          },
          tool_max_tokens: 10000,
          metadata: {
            max_tokens: 64000,
            reasoning: { effort: 'high' },
          },
        },
        {
          label: 'OpenRouter Llama 4 Scout',
          value: 'openrouter:meta-llama/llama-4-scout',
          subagent: { max_tokens: 10485760, hard_limit_tokens: 8388608 },
          compaction: {
            threshold_tokens: 8337408,
            messages_to_keep: 20,
            min_buffer_tokens: 1667482,
            input_limit_tokens: 4168704,
          },
          tool_max_tokens: 10000,
          metadata: {
            max_tokens: 64000,
            reasoning: { effort: 'high' },
          },
        },
        {
          label: 'OpenRouter Devstral 2512',
          value: 'openrouter:mistralai/devstral-2512',
          subagent: { max_tokens: 262144, hard_limit_tokens: 209715 },
          compaction: {
            threshold_tokens: 158515,
            messages_to_keep: 8,
            min_buffer_tokens: 31703,
            input_limit_tokens: 79258,
          },
          tool_max_tokens: 10000,
          metadata: {
            max_tokens: 64000,
            reasoning: { effort: 'high' },
          },
        },
        {
          label: 'OpenRouter Ministral 3 14B 2512',
          value: 'openrouter:mistralai/ministral-14b-2512',
          subagent: { max_tokens: 262144, hard_limit_tokens: 209715 },
          compaction: {
            threshold_tokens: 158515,
            messages_to_keep: 8,
            min_buffer_tokens: 31703,
            input_limit_tokens: 79258,
          },
          tool_max_tokens: 5000,
          metadata: {
            max_tokens: 64000,
            reasoning: { effort: 'high' },
          },
        },
        {
          label: 'OpenRouter Nemotron 3 Ultra 550B A55B (BaseTen FP4)',
          value: 'openrouter:nvidia/nemotron-3-ultra-550b-a55b',
          // Sized to the pinned BaseTen endpoint: 202800 ctx, 202800 max
          // output. Other endpoints differ -- see the routing note below.
          subagent: { max_tokens: 202800, hard_limit_tokens: 162240 },
          compaction: {
            // Shared 202800 pool. Derivation: 202800 - 16000 (output
            // reservation, sized to observed <10k real completions) -
            // ~50000 (system prompt + tool schemas invisible to the
            // compaction tracker) ~= 136800 visible-message budget;
            // 120000 leaves slack for the approximate counter.
            threshold_tokens: 120000,
            messages_to_keep: 8,
            min_buffer_tokens: 30000,
            input_limit_tokens: 75000,
          },
          tool_max_tokens: 10000,
          metadata: {
            max_tokens: 16000,
            reasoning: { effort: 'high' },
            openrouter_provider: {
              // No BF16 endpoint exists for this model. BaseTen is pinned for
              // uptime (100% over 30m) and its output cap == its window.
              // Alternates -- max_tokens above must be lowered to the target's
              // cap before flipping:
              //   together    512288 ctx, no reported output cap
              //   venice/fp8  256000 ctx,  32768 output cap
              //   deepinfra   262144 ctx,  16384 output cap
              // only: ['together'],
              // only: ['venice'],
              // only: ['deepinfra'],
              only: ['baseten'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter Nemotron 3 Super 120B A12B (DigitalOcean 1M)',
          value: 'openrouter:nvidia/nemotron-3-super-120b-a12b',
          // DigitalOcean serves a 1000000 window -- the only endpoint that
          // does. Ratios follow the Llama 4 Maverick entry (threshold 75% of
          // the window, min buffer 20% of threshold, input limit 50%).
          subagent: { max_tokens: 1000000, hard_limit_tokens: 800000 },
          compaction: {
            threshold_tokens: 750000,
            messages_to_keep: 20,
            min_buffer_tokens: 150000,
            input_limit_tokens: 375000,
          },
          tool_max_tokens: 10000,
          metadata: {
            // 64000 verified accepted on this endpoint -- OpenRouter reports
            // no max_completion_tokens for it, which is not the same as
            // "no cap", so the value was probed rather than assumed.
            max_tokens: 64000,
            reasoning: { effort: 'high' },
            openrouter_provider: {
              // The pin carries the context size: unpinned, OpenRouter's
              // default route for this model is DeepInfra at 262144 ctx with
              // a 16384 output cap. Verified live against the real MCP
              // schema set at the 2026-08-11 bench (oneOf-bearing, ~19.3k
              // prompt tokens; the set is ~20.8k now).
              //   deepinfra  bf16, 262144 ctx, 16384 output cap
              //   nebius     262144 ctx, no tool support at all
              // No quantizations filter: this endpoint reports quantization
              // "unknown", so any filter would exclude it.
              // only: ['deepinfra'],
              only: ['digitalocean'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter Nemotron 3 Super 120B A12B (Free)',
          value: 'openrouter:nvidia/nemotron-3-super-120b-a12b:free',
          // Single endpoint, served by Nvidia: 262144 ctx / 262144 max
          // output. No provider pin needed. Ratios match the other 262144
          // entries (Devstral, Ministral).
          subagent: { max_tokens: 262144, hard_limit_tokens: 209715 },
          compaction: {
            // Invariant: threshold + ~50000 uncounted overhead (system
            // prompt + tool schemas) + output reservation must stay
            // under the 262144 window. The old 158515/64000 pair
            // summed to ~272k -- over the window. 16000 output brings
            // it to ~224k with the same threshold.
            threshold_tokens: 158515,
            messages_to_keep: 8,
            min_buffer_tokens: 31703,
            input_limit_tokens: 79258,
          },
          tool_max_tokens: 10000,
          metadata: {
            // Verified accepted on this endpoint against the real MCP
            // payload as of the 2026-08-11 bench (6 tools then, 9 now).
            // Output reservation sized to observed usage (<10k real
            // completion tokens in the 2026-08-11 bench).
            max_tokens: 16000,
            reasoning: { effort: 'high' },
          },
        },
        {
          label: 'OpenRouter Muse Glimmer 30B (Together)',
          value: 'openrouter:meta/muse-glimmer-30b',
          // Three endpoints serve this model; the other two are rejected,
          // so Together is pinned with fallbacks off. The sizes below are
          // Together's (131072 ctx / 131072 max output, US, 99.8% uptime).
          // Re-check max_tokens and the compaction numbers before repinning
          // -- endpoint caps are not uniform across this model.
          subagent: { max_tokens: 131072, hard_limit_tokens: 104858 },
          compaction: {
            // Shared pool: the window covers input + output. The
            // 2026-08-11 ATCO bench killed 6/37 sessions with
            // context-length 400s under threshold 67072 / max_tokens
            // 64000: the compaction tracker counts only conversation
            // messages, so the ~32k-token system prompt and ~18k of
            // tool schemas were invisible and the threshold sat ABOVE
            // the reachable ceiling (131072 - 64000 output reservation
            // - 18250 tool input = 48822 text budget). Real answers
            // peak under 10k output tokens, so the 64k reservation was
            // pure waste. Derivation now: 131072 - 16000 (output) -
            // ~50000 (prompt + tool schemas, uncounted) ~= 65000
            // visible-message budget; threshold 45000 leaves slack for
            // the approximate counter and between-check growth.
            threshold_tokens: 45000,
            messages_to_keep: 8,
            min_buffer_tokens: 13414,
            input_limit_tokens: 33536,
          },
          tool_max_tokens: 10000,
          metadata: {
            // Verified live against the real MCP schema set as it stood
            // at the 2026-08-11 bench (then 6 tools, 76.5 KB, ~19.0k
            // prompt tokens): accepts the describe targets[] `oneOf` and
            // returns a run_pipelines tool call.
            //
            // STALE SIZE, 2026-08-19: review_mcp's schema set has grown to
            // 9 tools / 81.4 KB / ~20.8k tokens (11 tools / 87.7 KB /
            // ~22.5k where a project also has keyword + semantic search).
            // run_pipelines alone is 48.1 KB and define_fragment 19.7 KB.
            // The oneOf/tool-call findings above still hold; the BUDGETS
            // below were derived against the smaller set and are ~1.8k-3.5k
            // tokens optimistic. Re-bench before trusting them near a
            // context ceiling -- see the gemma-4-31b note above.
            // Output reservation sized to observed usage: largest real
            // completion in the 2026-08-11 bench was 9797 tokens
            // (thinking included), so 16000 covers it with margin
            // while freeing 48k of input budget vs the old 64000.
            max_tokens: 16000,
            reasoning: { effort: 'high' },
            openrouter_provider: {
              // No quantizations filter: Together reports precision
              // "--" (unknown), so any filter would exclude it.
              only: ['together'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter Nemotron 3.5 Lightning (Free)',
          value: 'openrouter:nvidia/nemotron-3.5-lightning:free',
          // Single endpoint, served by Nvidia (nvfp4): 1000000 ctx with a
          // 65536 output cap. Ratios follow the other 1M entry (Nemotron 3
          // Super on DigitalOcean): threshold 75% of the window, min buffer
          // 20% of threshold, input limit 50%.
          subagent: { max_tokens: 1000000, hard_limit_tokens: 800000 },
          compaction: {
            threshold_tokens: 750000,
            messages_to_keep: 20,
            min_buffer_tokens: 150000,
            input_limit_tokens: 375000,
          },
          tool_max_tokens: 10000,
          metadata: {
            // Under the endpoint's reported 65536 output cap.
            max_tokens: 64000,
            // No `reasoning` block: this endpoint advertises tools and
            // `reasoning`/`include_reasoning` but not `reasoning_effort`.
          },
        },
      ],
    },
    unstructuredDataLoader: {
      agentTypes: {
        [AGENT_TYPES.LANGCHAIN]: createLangChainConfig(),
        [AGENT_TYPES.LANGCHAIN_TXT]: createLangChainConfig(),
        [AGENT_TYPES.LANGCHAIN_JSON]: createLangChainConfig(),
      },
    },
    enricher: {
      models: [
        { label: 'LangChain', value: ENRICHER_PROVIDER_TYPES.LANGCHAIN },
      ],
      default: ENRICHER_PROVIDER_TYPES.LANGCHAIN,
      langchainModels: [
        {
          label: 'Claude Opus 5',
          value: 'anthropic:claude-opus-5',
        },
        {
          label: 'Claude Opus 5.5',
          value: 'anthropic:claude-opus-5-5',
        },
        {
          label: 'Claude Opus 4.6',
          value: 'anthropic:claude-opus-4-6',
        },
        {
          label: 'Claude Sonnet 5',
          value: 'anthropic:claude-sonnet-5',
        },
        {
          label: 'Claude Sonnet 5.5',
          value: 'anthropic:claude-sonnet-5-5',
        },
        {
          label: 'Claude Haiku 4.5',
          value: 'anthropic:claude-haiku-4-5-20251001',
        },
        {
          label: 'Anthropic Opus 4.1',
          value: 'anthropic:claude-opus-4-1-20250805',
        },
        {
          label: 'Azure ChatGPT GPT4o',
          value: 'azure:GPT4o:2024-08-01-preview',
        },
        {
          label: 'Azure Foundry ChatGPT 4-1',
          value: 'azure_foundry:gpt-4.1:2024-12-01-preview',
        },
        { label: 'Ollama GPT-OSS 20B', value: 'ollama:gpt-oss:20b' },
        {
          label: 'OpenRouter gpt-oss-20b (DeepInfra BF16)',
          value: 'openrouter:openai/gpt-oss-20b',
          metadata: {
            openrouter_provider: {
              only: ['deepinfra'],
              quantizations: ['bf16'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter gpt-oss-safeguard-20b (Nitro)',
          value: 'openrouter:openai/gpt-oss-safeguard-20b:nitro',
        },
        { label: 'OpenRouter GPT-5.4', value: 'openrouter:openai/gpt-5.4' },
        {
          label: 'OpenRouter Gemma 4 31B (Novita BF16)',
          value: 'openrouter:google/gemma-4-31b-it',
          metadata: {
            openrouter_provider: {
              only: ['novita'],
              quantizations: ['bf16'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter Gemma 4 26B A4B (Parasail BF16)',
          value: 'openrouter:google/gemma-4-26b-a4b-it',
          metadata: {
            openrouter_provider: {
              only: ['parasail'],
              quantizations: ['bf16'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter Llama 4 Maverick',
          value: 'openrouter:meta-llama/llama-4-maverick',
        },
        {
          label: 'OpenRouter Llama 4 Scout',
          value: 'openrouter:meta-llama/llama-4-scout',
        },
        {
          label: 'OpenRouter Devstral 2512',
          value: 'openrouter:mistralai/devstral-2512',
        },
        {
          label: 'OpenRouter Ministral 3 14B 2512',
          value: 'openrouter:mistralai/ministral-14b-2512',
        },
        {
          label: 'OpenRouter Muse Glimmer 30B (Together)',
          value: 'openrouter:meta/muse-glimmer-30b',
          metadata: {
            // See the shared catalog entry: the other two endpoints for this
            // model are rejected, so Together is pinned with fallbacks off.
            openrouter_provider: {
              only: ['together'],
              allow_fallbacks: false,
            },
          },
        },
        {
          label: 'OpenRouter Nemotron 3.5 Lightning (Free)',
          value: 'openrouter:nvidia/nemotron-3.5-lightning:free',
        },
      ],
    },
    linking: {
      models: [{ label: 'LangChain', value: LINKING_AGENT_TYPES.LANGCHAIN }],
      default: LINKING_AGENT_TYPES.LANGCHAIN,
      langchainModels: createLangChainConfig().models,
    },
    embeddings: {
      models: [
        { label: 'text-embedding-3-large', value: 'text-embedding-3-large' },
        { label: 'text-embedding-3-small', value: 'text-embedding-3-small' },
      ],
    },
  },
};

// Model exports
export const {
  reasoner: { models: reasonerModels },
  embeddings: { models: embeddingModels },
  enricher: {
    models: enricherProviderModels,
    langchainModels: enricherLangchainModels,
  },
  linking: {
    models: linkingProviderModels,
    langchainModels: linkingLangchainModels,
  },
  unstructuredDataLoader: { agentTypes: unstructuredDataLoaderAgentTypes },
} = modelsConfig.services;

/**
 * Resolve the full reasoner model option for a catalog model id.
 */
export function getReasonerModelOption(
  model: string,
): ReasonerModelOption {
  const opt = reasonerModels.find((o) => o.value === model);
  if (!opt) {
    throw new Error(`Reasoner model not in catalog: ${model}`);
  }
  return opt;
}

/**
 * Default model values from environment
 */
export {
  reasonerDefaultModel,
  enricherDefaultModel,
  linkingDefaultModel,
  embeddingDefaultModel,
  unstructuredDataLoaderDefaultModel,
  showModelSelector,
};

/**
 * Default enricher configuration
 */
export const enricherDefaults = {
  enricherType: modelsConfig.services.enricher.default,
  llmModel: enricherDefaultModel,
} as const;

/**
 * Default linking configuration
 */
export const linkingDefaults = {
  type: modelsConfig.services.linking.default as LinkingAgentType,
  modelName: linkingDefaultModel,
} as const;

/**
 * Generic function to get models for any service and agent type
 * For unstructuredDataLoader, agentType defaults to LANGCHAIN if not provided
 */
export const getModels = (
  service: ServiceType,
  agentType?: AgentType
): ModelOption[] => {
  if (service === SERVICES.UNSTRUCTURED_DATA_LOADER) {
    const type = agentType || AGENT_TYPES.LANGCHAIN;
    return (
      modelsConfig.services.unstructuredDataLoader.agentTypes[type]?.models ||
      []
    );
  }

  const serviceConfig = modelsConfig.services[service];
  return (serviceConfig as any).models || [];
};

/**
 * Generic function to get default model for any service and agent type
 * For unstructuredDataLoader, agentType defaults to LANGCHAIN if not provided
 */
export const getDefaultModel = (service: ServiceType): string => {
  switch (service) {
    case SERVICES.REASONER:
      return reasonerDefaultModel;
    case SERVICES.UNSTRUCTURED_DATA_LOADER:
      return unstructuredDataLoaderDefaultModel;
    case SERVICES.ENRICHER:
      return enricherDefaultModel;
    case SERVICES.LINKING:
      return linkingDefaultModel;
    case SERVICES.EMBEDDINGS:
      return embeddingDefaultModel;
    default:
      return '';
  }
};

/**
 * Generic function to get defaults for any service and agent type
 * For unstructuredDataLoader, agentType defaults to LANGCHAIN if not provided
 */
export const getDefaults = (
  service: ServiceType,
  agentType?: AgentType
): any => {
  if (service === SERVICES.UNSTRUCTURED_DATA_LOADER) {
    const type = agentType || AGENT_TYPES.LANGCHAIN;
    const config =
      modelsConfig.services.unstructuredDataLoader.agentTypes[type];
    return {
      modelName: getDefaultModel(service),
      ...(config?.defaults || {}),
    };
  }

  return {
    modelName: getDefaultModel(service),
  };
};

/**
 * Get default agent type for unstructured data loader based on file type
 */
export const getDefaultAgentTypeForFileType = (
  fileType: 'pdf' | 'txt' | 'json' | 'directory'
): AgentType => {
  switch (fileType) {
    case 'txt':
      return AGENT_TYPES.LANGCHAIN_TXT;
    case 'json':
      return AGENT_TYPES.LANGCHAIN_JSON;
    case 'pdf':
    case 'directory':
    default:
      return AGENT_TYPES.LANGCHAIN;
  }
};

/**
 * Get provider options based on strategy method for linking
 */
export const getLinkingProviderOptions = (method: string) => {
  if (method === LINKING_STRATEGY_TYPES.EMBEDDING_SIMILARITY) {
    return [
      { label: 'Embeddings API', value: EMBEDDINGS_API_TYPES.EMBEDDINGS_API },
    ];
  }

  if (method === LINKING_STRATEGY_TYPES.LLM) {
    return [{ label: 'LangChain', value: LINKING_AGENT_TYPES.LANGCHAIN }];
  }

  // Default fallback (shouldn't be used)
  return linkingProviderModels;
};

/**
 * Get model name options for LLM strategy in linking (LangChain only).
 */
export const getLinkingModelNameOptions = () => {
  return createLangChainConfig().models;
};

/**
 * Get default model name for LLM strategy in linking.
 */
export const getDefaultLinkingModelNameForAgentType = () => {
  return createLangChainConfig().models[0].value;
};
